import { useState, useEffect, useCallback } from 'react';

interface AccessibilitySettings {
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'x-large';
  reducedMotion: boolean;
  screenReaderMode: boolean;
  keyboardShortcuts: boolean;
  focusIndicators: 'default' | 'enhanced' | 'maximum';
}

interface KeyboardShortcut {
  key: string;
  modifiers?: ('ctrl' | 'alt' | 'shift' | 'meta')[];
  action: () => void;
  description: string;
  category: string;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  highContrast: false,
  fontSize: 'medium',
  reducedMotion: false,
  screenReaderMode: false,
  keyboardShortcuts: true,
  focusIndicators: 'default',
};

const FONT_SIZE_MAP = {
  small: '14px',
  medium: '16px',
  large: '18px',
  'x-large': '20px',
};

export function useAccessibility() {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    const saved = localStorage.getItem('accessibility-settings');
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  });

  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>([]);
  const [announcements, setAnnouncements] = useState<string[]>([]);

  // Apply settings to DOM
  useEffect(() => {
    const root = document.documentElement;
    
    // High contrast mode
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Font size
    root.style.setProperty('--base-font-size', FONT_SIZE_MAP[settings.fontSize]);
    
    // Reduced motion
    if (settings.reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }
    
    // Focus indicators
    root.setAttribute('data-focus-mode', settings.focusIndicators);
    
    // Screen reader mode
    root.setAttribute('data-screen-reader', settings.screenReaderMode.toString());
    
    // Save to localStorage
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
  }, [settings]);

  // Detect system preferences
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)');
    
    const handleMotionChange = (e: MediaQueryListEvent) => {
      if (e.matches && !localStorage.getItem('accessibility-settings')) {
        setSettings(prev => ({ ...prev, reducedMotion: true }));
      }
    };
    
    const handleContrastChange = (e: MediaQueryListEvent) => {
      if (e.matches && !localStorage.getItem('accessibility-settings')) {
        setSettings(prev => ({ ...prev, highContrast: true }));
      }
    };
    
    prefersReducedMotion.addEventListener('change', handleMotionChange);
    prefersHighContrast.addEventListener('change', handleContrastChange);
    
    // Check initial state
    if (prefersReducedMotion.matches && !localStorage.getItem('accessibility-settings')) {
      setSettings(prev => ({ ...prev, reducedMotion: true }));
    }
    if (prefersHighContrast.matches && !localStorage.getItem('accessibility-settings')) {
      setSettings(prev => ({ ...prev, highContrast: true }));
    }
    
    return () => {
      prefersReducedMotion.removeEventListener('change', handleMotionChange);
      prefersHighContrast.removeEventListener('change', handleContrastChange);
    };
  }, []);

  // Keyboard shortcut handler
  useEffect(() => {
    if (!settings.keyboardShortcuts) return;
    
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || 
          e.target instanceof HTMLTextAreaElement || 
          (e.target as HTMLElement).contentEditable === 'true') {
        return; // Don't trigger shortcuts in input fields
      }
      
      for (const shortcut of shortcuts) {
        const modifiersMatch = !shortcut.modifiers || shortcut.modifiers.every(mod => {
          switch (mod) {
            case 'ctrl': return e.ctrlKey;
            case 'alt': return e.altKey;
            case 'shift': return e.shiftKey;
            case 'meta': return e.metaKey;
            default: return true;
          }
        });
        
        if (modifiersMatch && e.key === shortcut.key) {
          e.preventDefault();
          shortcut.action();
          announce(`${shortcut.description}`);
          break;
        }
      }
    };
    
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [shortcuts, settings.keyboardShortcuts]);

  const updateSetting = useCallback(<K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleHighContrast = useCallback(() => {
    setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }));
    announce(settings.highContrast ? 'High contrast disabled' : 'High contrast enabled');
  }, [settings.highContrast]);

  const increaseFontSize = useCallback(() => {
    const sizes: AccessibilitySettings['fontSize'][] = ['small', 'medium', 'large', 'x-large'];
    const currentIndex = sizes.indexOf(settings.fontSize);
    if (currentIndex < sizes.length - 1) {
      const newSize = sizes[currentIndex + 1];
      setSettings(prev => ({ ...prev, fontSize: newSize }));
      announce(`Font size increased to ${newSize}`);
    }
  }, [settings.fontSize]);

  const decreaseFontSize = useCallback(() => {
    const sizes: AccessibilitySettings['fontSize'][] = ['small', 'medium', 'large', 'x-large'];
    const currentIndex = sizes.indexOf(settings.fontSize);
    if (currentIndex > 0) {
      const newSize = sizes[currentIndex - 1];
      setSettings(prev => ({ ...prev, fontSize: newSize }));
      announce(`Font size decreased to ${newSize}`);
    }
  }, [settings.fontSize]);

  const resetFontSize = useCallback(() => {
    setSettings(prev => ({ ...prev, fontSize: 'medium' }));
    announce('Font size reset to default');
  }, []);

  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    setShortcuts(prev => [...prev.filter(s => s.key !== shortcut.key || 
      JSON.stringify(s.modifiers) !== JSON.stringify(shortcut.modifiers)), shortcut]);
  }, []);

  const unregisterShortcut = useCallback((key: string, modifiers?: KeyboardShortcut['modifiers']) => {
    setShortcuts(prev => prev.filter(s => 
      s.key !== key || JSON.stringify(s.modifiers) !== JSON.stringify(modifiers)
    ));
  }, []);

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    setAnnouncements(prev => [...prev, message]);
    
    // Create temporary screen reader announcement
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    // Remove after announcement is read
    setTimeout(() => {
      document.body.removeChild(announcement);
      setAnnouncements(prev => prev.filter(a => a !== message));
    }, 1000);
  }, []);

  const skipToContent = useCallback(() => {
    const mainContent = document.querySelector('main, [role="main"], #main-content');
    if (mainContent instanceof HTMLElement) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth' });
      announce('Skipped to main content');
    }
  }, [announce]);

  const focusTrap = useCallback((containerRef: React.RefObject<HTMLElement>) => {
    if (!containerRef.current) return () => {};
    
    const focusableElements = containerRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };
    
    containerRef.current.addEventListener('keydown', handleTabKey);
    firstElement?.focus();
    
    return () => {
      containerRef.current?.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  return {
    settings,
    updateSetting,
    toggleHighContrast,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    registerShortcut,
    unregisterShortcut,
    announce,
    skipToContent,
    focusTrap,
    announcements,
  };
}
